import { Client, ClientWithLocations } from "@/types/clients.types"
import ConfirmModal from "@/UI/Modals/ConfirmModal"
import { faEdit, faEllipsis, faTrashCan } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Dropdown, Menu, message, Table } from "antd"
import { useTranslation } from "react-i18next"
import React from "react"
import { Location } from "@/types/orders.types"
import CopyText from "@/modules/shared/CopyText"
import { DropdownItemContainer } from "@/modules/profile/OrganizationSettings/components/OrgUsersActionsMenu"
import { useMediaQuery } from "usehooks-ts"

interface ClientsTableProps {
  clients: ClientWithLocations[]
  openEditClient: (client: Client) => void
  deleteClients: (id: string) => Promise<void>
}

const columns = (
  t: (key: string) => string,
  openEditClient: (client: Client) => void,
  deleteClients: (id: string) => Promise<void>
) => [
  {
    title: t("common.name").toUpperCase(),
    key: "1",
    width: "15%",
    render: (client: Client) => {
      return (
        <div key={client.id}>
          <p className="my-auto whitespace-nowrap text-ellipsis overflow-hidden">{client.name}</p>
        </div>
      )
    },
  },
  {
    title: t("common.id").toUpperCase(),
    key: "clientId",
    dataIndex: "clientId",
    render: (clientId: string) => {
      return (
        <div className="my-auto whitespace-nowrap text-ellipsis overflow-hidden text-sm text-gray-600">
          <CopyText text={clientId} />
        </div>
      )
    },
  },
  {
    title: t("common.email").toUpperCase(),
    key: "2",
    dataIndex: "email",
    render: (email: string) => {
      return (
        <div className="my-auto whitespace-nowrap text-ellipsis overflow-hidden text-sm text-gray-600">
          <CopyText text={email} />
        </div>
      )
    },
  },
  {
    title: t("common.phone").toUpperCase(),
    key: "3",
    dataIndex: "phone",
    render: (phone: string) => {
      return (
        <div className="my-auto whitespace-nowrap text-ellipsis overflow-hidden text-sm text-gray-600">
          <CopyText text={phone} />
        </div>
      )
    },
  },
  {
    title: t("common.location").toUpperCase(),
    key: "location",
    dataIndex: "locations",
    width: "25%",
    render: (locations?: Location[]) => {
      return (
        <div className="flex flex-col">
          {locations?.map((location, idx) => (
            <div key={idx} className="flex-col items-center">
              <p className="my-auto whitespace-nowrap text-ellipsis overflow-hidden">
                {location.address}
              </p>
              <p className="my-auto whitespace-nowrap text-ellipsis overflow-hidden">
                {location.buildingNumber}
              </p>
              <p className="my-auto whitespace-nowrap text-ellipsis overflow-hidden">
                {location.postalCode}
              </p>
            </div>
          ))}
        </div>
      )
    },
  },
  {
    title: t("common.portalLink").toUpperCase(),
    key: "url",
    dataIndex: "url",
    render: (url: string) => {
      return <CopyText text={url} label={t("common.portalLink")} />
    },
  },
  {
    key: "6",
    width: "10%",
    render: (client: Client) => {
      const onDelete = async () => {
        try {
          if (!client.id) {
            message.error(t("developers.Clients.deleteClientError"))
            return
          }
          await deleteClients(client.id)
          message.success(t("developers.Clients.deleteClientSuccess"))
        } catch (error) {
          message.error(t("developers.Clients.deleteClientError"))
        }
      }

      return (
        <Dropdown
          trigger={["click"]}
          overlay={
            <Menu id="api-keys-table-dropdown">
              <Menu.Item onClick={() => openEditClient(client)}>
                <DropdownItemContainer text={t("common.edit")} icon={faEdit} />
              </Menu.Item>
              <Menu.Item disabled={client.name === t("profile.Clients.createDefaultClientName")}>
                <ConfirmModal
                  disabled={client.name === t("profile.Clients.createDefaultClientName")}
                  button={
                    <div className="h-full">
                      <DropdownItemContainer text={t("common.remove")} icon={faTrashCan} />
                    </div>
                  }
                  content={{
                    type: "danger",
                    title: t("profile.Clients.deleteClient"),
                    description: t("profile.Clients.deleteClientsConfirmation"),
                    cancelText: t("common.cancel"),
                    okText: t("common.yesDelete"),
                  }}
                  onConfirm={onDelete}
                />
              </Menu.Item>
            </Menu>
          }
        >
          <div className="flex items-center justify-center">
            <FontAwesomeIcon
              className="flex-shrink-0 h-4 text-gray-500 text-sm cursor-pointer"
              icon={faEllipsis}
            />
          </div>
        </Dropdown>
      )
    },
  },
]

const ClientsTable: React.FC<ClientsTableProps> = ({ clients, openEditClient, deleteClients }) => {
  const { t } = useTranslation()

  const isMobile = useMediaQuery("(max-width: 950px)")

  return (
    <Table
      id="api-keys-table"
      className="!mt-0"
      rowKey={"id"}
      tableLayout={isMobile ? "auto" : "fixed"}
      scroll={{ x: "100%" }}
      dataSource={clients}
      columns={columns(t, openEditClient, deleteClients)}
    />
  )
}

export default ClientsTable
